import React, { useEffect, useState } from "react";
import Select from "react-select";
import DateRangePickerComponent from "./CustomDateRangePicker";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronCircleLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";

const NUM_STYLE = {
  width: "90px",
  fontSize: "12px",
  height: "28px",
  padding: "2px 6px",
};
const showToast = (icon, title) => {
  Swal.fire({
    toast: true,
    position: "top-end",
    icon,
    title,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
};


const RequirementAssessment = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [selectedRequirement, setSelectedRequirement] = useState([]);
  const [groupedItems, setGroupedItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [applyLedger, setApplyLedger] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAllItems, setSelectAllItems] = useState(false);

  const sewadarId = localStorage.getItem("sewadarId");
  const token = localStorage.getItem("token");
  const centerCode = localStorage.getItem("code");

  const navigate = useNavigate();
  const location = useLocation();
  const assessmentId = location.state?.id;

  /* ---------------- PROJECTS ---------------- */
  useEffect(() => {
    apiClient.get(`${SERVER_BASE}/area-projects`).then(res => {
      setProjects(res.data.map(p => ({ value: p.id, label: p.projectName })));
    });
  }, []);

  /* ---------------- LEDGER OPTIONS ---------------- */
  useEffect(() => {
    const fetchLedgerOptions = async () => {
      try {
        const response = await apiClient.get(
          `${SERVER_BASE}/ledger-groups`,
          {
            params: { code: centerCode },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const ledgerGroupOptions = response.data.map(g => ({
          value: g.groupName,
          label: g.groupName,
          ledgerId: g.groupCode,
          type: "LEDGER_GROUP",
        }));

        // ➕ Department option
        const departmentOption = {
          value: "Department",
          label: "Department",
          ledgerId: "Department",
          type: "DEPARTMENT",
        };

        setLedgerOptions([
          ...ledgerGroupOptions,
          departmentOption,
        ]);
      } catch (err) {
        console.error("Ledger group option load error:", err);
      }
    };

    fetchLedgerOptions();
  }, []);

  /* ---------------- LOAD ASSESSMENT (EDIT) ---------------- */
  useEffect(() => {
    if (!assessmentId || !ledgerOptions.length) return;

    const loadAssessment = async () => {
      setPageLoading(true);
      try {
        const res = await apiClient.get(
          `${SERVER_BASE}/requirement-assessments/${assessmentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { assessment, requirements, items, departmentRows } = res.data;

        setSelectedProject({
          value: assessment.projectId,
          label: assessment.projectName,
        });

        setSelectedRequirement(
          (requirements || []).map(r => {
            let prefix = "";

            if (r.localCount > 0 && r.zonalCount > 0) {
              prefix = "L+Z";
            } else if (r.localCount > 0) {
              prefix = "L";
            } else if (r.zonalCount > 0) {
              prefix = "Z";
            }

            return {
              value: r.id,
              label: `${r.department} [ ${prefix} ]`,
            };
          })
        );


        const grouped = {};

        departmentRows.forEach(d => {
          if (!grouped[d.itemId]) {
            const it = items.find(i => i.itemId === d.itemId);
            grouped[d.itemId] = {
              itemId: d.itemId,
              printName: it?.printName || "",
              uom: it?.uom || "",
              departments: [],
              ledgers: [],
            };
          }

          grouped[d.itemId].departments.push({
            department: d.department,
            zonalQty: Number(d.zonalQty || 0),
            localQty: Number(d.localQty || 0),
            totalQty: Number(d.totalQty || 0),
            remarks: d.remarks || "",
          });
        });

        items.forEach(it => {
          const g = grouped[it.itemId];
          if (!g) return;

          g.availableQty = Number(it.availableQty || 0);
          g.estimation = Number(it.requirementQty || 0); // demand
          g.assessment = String(it.addLessQty ?? "");
          g.finalRequirement =
            Number(it.requirementQty || 0) + Number(it.addLessQty || 0);


          let parsedLedgers = [];
          try {
            parsedLedgers =
              typeof it.ledgers === "string"
                ? JSON.parse(it.ledgers)
                : it.ledgers || [];
          } catch {
            parsedLedgers = [];
          }

          g.ledgers =
            parsedLedgers.length > 0
              ? parsedLedgers.map(l => ({
                ledger:
                  ledgerOptions.find(
                    lo => lo.ledgerId === l.ledgerId || lo.value === l.ledgerId
                  ) || null,
                qty: l.qty,
              }))
              : [{ ledger: null, qty: "" }];
        });

        setGroupedItems(Object.values(grouped));
      } catch (err) {
        Swal.fire("Error", "Unable to load assessment", "error");
      } finally {
        setPageLoading(false);
      }
    };

    loadAssessment();
  }, [assessmentId, ledgerOptions]);

  /* ---------------- REQUIREMENTS ---------------- */
  const fetchRequirements = async () => {
    const [start, end] = dateRange;
    if (!start || !end || !selectedProject) {
      Swal.fire({ toast: true, icon: "warning", title: "Select Date & Project", timer: 2000 });
      return;
    }

    const res = await apiClient.get(`${SERVER_BASE}/getProjectRequirementByFilter`, {
      params: {
        startDate: moment(start).format("YYYY-MM-DD"),
        endDate: moment(end).format("YYYY-MM-DD"),
        projectId: selectedProject.value,
      },
      headers: { Authorization: `Bearer ${token}` },
    });

    setRequirements([
      { value: "__ALL__", label: "Select All" },
      ...res.data.map(r => {
        let prefix = "";

        if (r.localCount > 0 && r.zonalCount > 0) {
          prefix = "L+Z";
        } else if (r.localCount > 0) {
          prefix = "L";
        } else if (r.zonalCount > 0) {
          prefix = "Z";
        }

        return {
          value: r.id,
          label: `${r.department} [ ${prefix} ]`,
        };
      }),
    ]);

  };

  const handleRequirementChange = (selected) => {
    if (!selected) return setSelectedRequirement([]);
    if (selected.some(o => o.value === "__ALL__")) {
      setSelectedRequirement(requirements.filter(o => o.value !== "__ALL__"));
    } else {
      setSelectedRequirement(selected);
    }
  };

  /* ---------------- INVENTORY ---------------- */
  const fetchAvailableQty = async (itemId) => {
    const res = await apiClient.get(`${SERVER_BASE}/getAvailableQuantityItemWise`, {
      params: { itemId },
    });
    return Number(res.data.availableQty || 0);
  };

  const fetchInventories = async () => {
    if (!selectedRequirement.length) {
      Swal.fire({ toast: true, icon: "warning", title: "Select requirement", timer: 2000 });
      return;
    }

    setLoadingInventory(true);
    try {
      const ids = selectedRequirement.map(r => r.value).join(",");
      const res = await apiClient.get(
        `${SERVER_BASE}/getProjectRequirementItemsByIds`,
        { params: { ids }, headers: { Authorization: `Bearer ${token}` } }
      );

      const grouped = {};
      for (const row of res.data) {
        if (!grouped[row.itemId]) {
          grouped[row.itemId] = {
            itemId: row.itemId,
            printName: row.printName,
            uom: row.uom,
            departments: [],
            ledgers: [{ ledger: null, qty: "" }],
          };
        }

        grouped[row.itemId].departments.push({
          department: row.department,
          zonalQty: Number(row.zonalQty || 0),
          localQty: Number(row.localQty || 0),
          totalQty: Number(row.totalQty || 0),
          remarks: row.remarks || "",
        });
      }

      const final = [];
      for (const item of Object.values(grouped)) {
        const total = item.departments.reduce((s, d) => s + d.totalQty, 0);
        const availableQty = await fetchAvailableQty(item.itemId);
        final.push({
          ...item,
          availableQty,          // Stock
          estimation: total,     // Demand only
          assessment: "",
          finalRequirement: total, // estimation ± assessment
        });
      }

      setGroupedItems(final);
    } finally {
      setLoadingInventory(false);
    }
  };

  /* ---------------- LEDGER HANDLERS ---------------- */
  const updateLedger = (itemId, rowIndex, field, value) => {
    setGroupedItems(prev =>
      prev.map(it => {
        if (it.itemId !== itemId) return it;
        const ledgers = [...it.ledgers];
        ledgers[rowIndex][field] = value;
        return { ...it, ledgers };
      })
    );
  };

  const addLedgerRow = (itemId) => {
    setGroupedItems(prev =>
      prev.map(it =>
        it.itemId === itemId
          ? { ...it, ledgers: [...it.ledgers, { ledger: null, qty: "" }] }
          : it
      )
    );
  };

  const removeLedgerRow = (itemId, index) => {
    setGroupedItems(prev =>
      prev.map(it =>
        it.itemId === itemId
          ? { ...it, ledgers: it.ledgers.filter((_, i) => i !== index) }
          : it
      )
    );
  };

  /* ---------------- SAVE ---------------- */
  const handleSave = async () => {
    for (const item of groupedItems) {
      let sum = 0;

      for (const l of item.ledgers) {
        if (!l.ledger || l.qty === "") {
          showToast("error", "Ledger and quantity required");
          return;
        }
        sum += Number(l.qty);
      }

      if (sum !== item.finalRequirement) {
        showToast(
          "error",
          `${item.printName}: Ledger total must equal Actual`
        );
        return;
      }
    }

    const payload = {
      projectId: selectedProject.value,
      projectName: selectedProject.label,
      requirementIds: selectedRequirement.map(r => r.value),
      items: groupedItems.map(it => ({
        itemId: it.itemId,
        printName: it.printName,
        uom: it.uom,
        availableQty: it.availableQty,
        requirementQty: it.estimation,
        addLessQty: Number(it.assessment || 0),
        actualQty: it.finalRequirement,
        ledgers: it.ledgers.map(l => ({
          ledgerId: l.ledger?.ledgerId || l.ledger?.value,
          name: l.ledger?.label,
          qty: Number(l.qty),
        })),
      })),
    };

    setSaving(true);
    try {
      if (assessmentId) {
        await apiClient.put(
          `${SERVER_BASE}/update-requirement-assessment/${assessmentId}`,
          { ...payload, updatedBy: sewadarId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await apiClient.post(
          `${SERVER_BASE}/save-requirement-assessment`,
          { ...payload, createdBy: sewadarId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      showToast("success", "Saved successfully");

      setTimeout(() => {
        navigate(-1);
      }, 800);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- UI ---------------- */
  if (pageLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <h4>Loading Assessment...</h4>
      </div>
    );
  }
  const smallSelectStyles = {
    container: (base) => ({
      ...base,
      flex: 1,
    }),
    control: (base) => ({
      ...base,
      minHeight: "28px",
      height: "28px",
      fontSize: "12px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 6px",
    }),
    input: (base) => ({
      ...base,
      fontSize: "12px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    option: (base) => ({
      ...base,
      fontSize: "12px",
      padding: "4px 8px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "28px",
    }),
  };


  return (
    <div className="formcontainer m-4">
      {/* HEADER */}
      <div className="row align-items-end mb-3">
        {/* BACK BUTTON */}
        <div className="col-md-1">
          <button
            className="btn btn-secondary btn-sm w-100"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faChevronCircleLeft} /> Back
          </button>
        </div>

        {/* TITLE */}
        <div className="col-md-3">
          <h4 className="mb-0">Assessment</h4>
        </div>

        {/* DATE RANGE */}
        <div className="col-md-3">
          <DateRangePickerComponent value={dateRange} onChange={setDateRange} />
        </div>

        {/* PROJECT */}
        <div className="col-md-3">
          <Select
            options={projects}
            value={selectedProject}
            onChange={setSelectedProject}
          />
        </div>

        {/* GET REQUIREMENTS */}
        <div className="col-md-2">
          <button
            className="btn btn-primary w-100"
            onClick={fetchRequirements}
          >
            Get Requirements
          </button>
        </div>
      </div>


      {(requirements.length > 0 || selectedRequirement.length > 0) ?
        <div className="row mb-4">
          <div className="col-md-10">
            <Select
              isMulti
              options={requirements}
              value={selectedRequirement}
              onChange={handleRequirementChange}
              closeMenuOnSelect={false}
            />
          </div>
          <div className="col-md-2">
            <button
              className="btn btn-success w-100"
              onClick={fetchInventories}
              disabled={loadingInventory}
            >
              {loadingInventory ? "Loading..." : "Get Inventories"}
            </button>
          </div>
        </div> : null}

      {/* ITEMS */}
      {groupedItems.map((item, idx) => {
        const rows = Math.max(item.departments.length + 1, item.ledgers.length);
        const allocatedQty = item.ledgers.reduce(
          (s, l) => s + Number(l.qty || 0),
          0
        );

        const unallocatedQty = item.finalRequirement - allocatedQty;

        return (
          <div key={item.itemId}>
            <div className="row align-items-end mb-3">
              {/* ITEM NAME – 40% */}
              <div className="col-md-5">
                <h6 className="mb-0">
                  {idx + 1}. {item.printName}
                </h6>
              </div>

              {/* APPLY LEDGER SECTION – 60% (FIRST ITEM ONLY) */}
              {idx === 0 && (
                <div className="col-md-7">
                  <div className="row align-items-end g-2">
                    {/* APPLY LEDGER SELECT */}
                    <div className="col-md-8">
                      <Select
                        options={ledgerOptions}
                        value={applyLedger}
                        onChange={setApplyLedger}
                        placeholder="Apply Ledger"
                      // styles={smallSelectStyles}
                      />
                    </div>

                    {/* APPLY BUTTON */}
                    <div className="col-md-2">
                      <button
                        className="btn btn-primary btn-sm w-100"
                        disabled={!applyLedger || selectedItems.length === 0}
                        onClick={() => {
                          setGroupedItems(prev =>
                            prev.map(it =>
                              selectedItems.includes(it.itemId)
                                ? {
                                  ...it,
                                  ledgers: [
                                    {
                                      ledger: applyLedger,
                                      qty: it.finalRequirement,
                                    },
                                  ],
                                }
                                : it
                            )
                          );
                        }}
                      >
                        Apply
                      </button>
                    </div>

                    {/* SELECT ALL */}
                    <div className="col-md-2 d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        style={{ transform: "scale(1.2)", cursor: "pointer" }}
                        checked={selectAllItems}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectAllItems(checked);
                          setSelectedItems(
                            checked ? groupedItems.map(it => it.itemId) : []
                          );
                        }}
                      />
                      <label className="mb-0">Select All</label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ fontSize: "12px" }}>
              <table className="table table-bordered table-striped align-middle text-center">
                <thead>
                  <tr>
                    <th className="align-middle">Stock</th>
                    <th className="align-middle">Department</th>
                    <th className="align-middle">Zonal</th>
                    <th className="align-middle">Local</th>
                    <th className="align-middle">Total</th>
                    <th className="align-middle">Remarks</th>
                    <th className="align-middle">Available</th>
                    <th className="align-middle">Requirement</th>
                    <th className="align-middle">Add / Less</th>
                    <th className="align-middle">Actual</th>
                    <th className="align-middle">Provider</th>
                    <th className="align-middle">Qty</th>
                    <th className="align-middle">UOM</th>
                    <th className="align-middle">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.itemId)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedItems(prev =>
                            checked
                              ? [...prev, item.itemId]
                              : prev.filter(id => id !== item.itemId)
                          );
                          if (!checked) setSelectAllItems(false);
                        }}
                      />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {Array.from({ length: rows }).map((_, r) => {
                    const isTotal = r === 0;
                    const d = item.departments[r - 1];
                    const l = item.ledgers[r];

                    return (
                      <tr key={r}>
                        {/* STOCK */}
                        <td className="align-middle">
                          {isTotal ? item.availableQty : ""}
                        </td>

                        {/* DEPARTMENT */}
                        <td className="align-middle">
                          {isTotal ? "Total" : d?.department}
                        </td>

                        {/* ZONAL / LOCAL / TOTAL */}
                        {["zonalQty", "localQty", "totalQty"].map((k, i) => (
                          <td key={i} className="align-middle">
                            {isTotal
                              ? k === "totalQty"
                                ? item.departments.reduce((s, x) => s + (x.totalQty || 0), 0) + item.availableQty
                                : item.departments.reduce((s, x) => s + (x[k] || 0), 0)
                              : d?.[k] ?? ""}

                          </td>
                        ))}

                        {/* REMARKS */}
                        <td className="align-middle">
                          {!isTotal ? d?.remarks || "" : ""}
                        </td>

                        {/* AVAILABLE */}
                        <td className="align-middle">
                          {isTotal ? item.availableQty : ""}
                        </td>

                        {/* REQUIREMENT */}
                        <td className="align-middle">
                          {isTotal ? item.estimation : ""}
                        </td>

                        {/* ADD / LESS */}
                        <td className="align-middle">
                          {isTotal && (
                            <input
                              className="form-control text-center mx-auto"
                              style={NUM_STYLE}
                              value={item.assessment}
                              onChange={(e) =>
                                setGroupedItems(prev =>
                                  prev.map(it =>
                                    it.itemId === item.itemId
                                      ? {
                                        ...it,
                                        assessment: e.target.value,
                                        finalRequirement:
                                          it.estimation + Number(e.target.value || 0),
                                      }
                                      : it
                                  )
                                )
                              }
                            />
                          )}
                        </td>

                        {/* ACTUAL */}
                        <td className="align-middle">
                          {isTotal ? item.finalRequirement : ""}
                        </td>

                        {/* PROVIDER */}
                        <td className="align-middle">
                          {l && (
                            <div className="d-flex justify-content-center align-items-center">
                              <Select
                                options={ledgerOptions}
                                value={l.ledger}
                                onChange={(v) =>
                                  updateLedger(item.itemId, r, "ledger", v)
                                }
                                styles={smallSelectStyles}
                              />

                              {r === item.ledgers.length - 1 && (
                                <button
                                  className="btn btn-sm btn-outline-primary ms-1"
                                  onClick={() => addLedgerRow(item.itemId)}
                                >
                                  +
                                </button>
                              )}

                              {item.ledgers.length > 1 && (
                                <button
                                  className="btn btn-sm btn-outline-danger ms-1"
                                  onClick={() => removeLedgerRow(item.itemId, r)}
                                >
                                  −
                                </button>
                              )}
                            </div>
                          )}
                        </td>

                        {/* QTY */}
                        <td className="align-middle">
                          {l && (
                            <input
                              className="form-control text-center mx-auto"
                              style={NUM_STYLE}
                              value={l.qty}
                              onChange={(e) =>
                                updateLedger(item.itemId, r, "qty", e.target.value)
                              }
                            />
                          )}
                        </td>

                        {/* UOM */}
                        <td className="align-middle">
                          {isTotal ? item.uom : ""}
                        </td>

                        <td className="align-middle" />
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="row mt-2 mb-2">
              <div className="col-md-12">
                <div className="d-flex justify-content-end gap-4 fw-semibold">

                  <div>
                    Allocated :
                    <span className="ms-1 text-success">
                      {allocatedQty}
                    </span>
                  </div>

                  <div>
                    Unallocated :
                    <span
                      className={`ms-1 ${unallocatedQty === 0
                        ? "text-success"
                        : unallocatedQty > 0
                          ? "text-primary"
                          : "text-danger"
                        }`}
                    >
                      {unallocatedQty}
                    </span>
                  </div>

                </div>
              </div>
            </div>

            <hr style={{ borderTop: "3px solid #000" }} />
          </div>
        );
      })}

      {/* SAVE */}
      {groupedItems.length > 0 && (
        <div className="text-end mb-4">
          <button
            className="btn btn-success px-4"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : assessmentId
                ? "Update Assessment"
                : "Save Assessment"}
          </button>
        </div>
      )}
    </div>
  );
};

export default RequirementAssessment;
